import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IChainMenu } from '../../../interfaces/menu';
import { isFormChanged } from '../../../shared/function';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MenuStoreService } from '../../../services/menu/menu-store.service';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { MatSelectChange } from '@angular/material/select';

type ChainMenuForm = {
  name: FormControl<string>;
  language: FormControl<string | null>;
  menuFile: FormControl<File | null>;
  iconFile: FormControl<File | null>;
  happyHours: FormControl<boolean>;
  hidden: FormControl<boolean>;
  restaurants: FormControl<number[] | null>;
  attachedToAll: FormControl<boolean>;
};

type ChainMenuFormValue = ReturnType<FormGroup<ChainMenuForm>['getRawValue']>;

@UntilDestroy()
@Component({
  selector: 'app-chain-menu',
  templateUrl: './chain-menu.component.html',
  styleUrls: ['./chain-menu.component.scss'],
})
export class ChainMenuComponent implements OnInit {

  @ViewChild('inputFile') inputMenu!: ElementRef<HTMLInputElement>;
  private edit = false;
  public menuData!: IChainMenu;
  public restaurants!: IRestaurantInfo[];

  @Input() set menu(menu: IChainMenu) {
    this.menuData = menu;
    this.form.setValidators(
      isFormChanged({
        name: menu.name,
        happyHours: menu.happyHours,
        hidden: menu.hidden,
      })
    );
    this.attachedRestaurants = this.getAttachedRestaurants(this.restaurants, menu);
  }

  @Input() set isEdit(value: boolean) {
    this.edit = value;
    this.resetInputFiles();
  }
  @Input() chainId!: number;
  @Input() isMainMenu = true;
  @Input() set allRestaurants(restaurants: IRestaurantInfo[]) {
    this.restaurants = restaurants;
    this.attachedRestaurants = this.getAttachedRestaurants(restaurants, this.menuData);
  }
  menuFile: File | null = null;
  iconFile: File | null = null;
  isUpdating = false;

  // tslint:disable-next-line:adjacent-overload-signatures
  get menu(): IChainMenu {
    return this.menuData;
  }

  // tslint:disable-next-line:adjacent-overload-signatures
  get isEdit(): boolean {
    return this.edit;
  }

  form = new FormGroup<ChainMenuForm>({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(3)] }),
    language: new FormControl<string | null>(null),
    menuFile: new FormControl<File | null>(null),
    iconFile: new FormControl<File | null>(null),
    happyHours: new FormControl(false, { nonNullable: true }),
    hidden: new FormControl(false, { nonNullable: true }),
    restaurants: new FormControl<number[] | null>(null),
    attachedToAll: new FormControl(false, { nonNullable: true }),
  });
  attachedRestaurants: IRestaurantInfo[] | null = null;

  constructor(private menuState: MenuStoreService) {}

  ngOnInit(): void {
    this.initializeForm();

    this.form.controls.attachedToAll.valueChanges.subscribe((checked) => {
      this.form.controls.restaurants.reset({
        value: checked ? null : this.menuData?.attachedRestaurantIds,
        disabled: checked
      });
    });
  }

  update(data: {
    id: number;
    values: ChainMenuFormValue;
  }): void {
    this.isUpdating = true;
    let updatedRestaurants!: number[] | null;

    if (data.values.attachedToAll) {
      updatedRestaurants = [];
    } else if ((!data.values.attachedToAll && data.values.restaurants?.length === 0) || this.isNullInArray(data.values.restaurants)) {
      updatedRestaurants = null;
    } else {
      updatedRestaurants = data.values.restaurants;
    }

    this.menuState
      .updateChainMenu(data.id, this.chainId, updatedRestaurants, {
        name: data.values.name,
        language: data.values.language,
        happyHours: data.values.happyHours,
        hidden: data.values.hidden,
        menuFile: data.values.menuFile,
        iconFile: data.values.iconFile,
      })
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          this.isUpdating = false;
        },
        () => (this.isUpdating = false)
      );
  }

  public isNullInArray(restaurants: number[] | null): boolean {
    return restaurants?.length === 1 && restaurants[0] === null;
  }

  chainMenuDelete(chainId: number, menuId: number): void {
    this.isUpdating = true;
    this.menuState
      .removeChainMenu(chainId, menuId)
      .pipe(untilDestroyed(this))
      .subscribe(
        () => (this.isUpdating = false),
        () => (this.isUpdating = false)
      );
  }

  onMenuFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files;
    if (file) {
      this.menuFile = file[0];
      this.form.controls.menuFile.setValue(file[0]);
      this.form.controls.menuFile.markAsDirty();
    } else {
      this.form.controls.menuFile.setValue(null);
      this.form.controls.menuFile.markAsPristine();
    }
    this.form.updateValueAndValidity();
  }

  onIconFileChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files;
    if (file) {
      this.iconFile = file[0];
      this.form.controls.iconFile.setValue(file[0]);
      this.form.controls.iconFile.markAsDirty();
    } else {
      this.form.controls.iconFile.setValue(null);
      this.form.controls.iconFile.markAsPristine();
    }
    this.form.updateValueAndValidity();
  }

  resetInputFiles(): void {
    setTimeout(() => {
      this.inputMenu.nativeElement.value = '';
    }, 0);
    this.menuFile = null;
    this.iconFile = null;
    this.form.patchValue({ menuFile: null });
    this.form.patchValue({ iconFile: null });
  }

  public setSelectedRestaurants(event: MatSelectChange): void {
    const selectedOptions = event.value as number[];
  }

  private getAttachedRestaurants(restaurants: IRestaurantInfo[], menu: IChainMenu): IRestaurantInfo[] | null {

    if (!restaurants || !menu) {
      return null;
    } else if (menu.attachedRestaurantIds){
        return restaurants.filter(r => menu.attachedRestaurantIds?.some(id => id === r.id));
    }
    return null;
  }

  private initializeForm(): void {
    this.form.reset({
      name: this.menuData.name,
      language: this.menuData.language,
      menuFile: null,
      iconFile: null,
      happyHours: this.menuData.happyHours,
      hidden: this.menuData.hidden,
      restaurants: this.menuData.attachedToAll ? null : this.menuData.attachedRestaurantIds,
      attachedToAll: this.menuData.attachedToAll,
    });

    if (this.menuData.attachedToAll) {
      this.form.controls.restaurants.disable({ emitEvent: false });
    } else {
      this.form.controls.restaurants.enable({ emitEvent: false });
    }
  }
}
